import type { AxiosError } from "axios";

import { adminUiCopy } from "@/copy/adminUiCopy";
import type { ApiError } from "@/types/api";
import {
  coercePublicErrorResponse,
  formatPublicErrorForDisplay,
} from "@/utils/errors/publicErrorContract";
import { readCorrelationIdFromHeaders } from "@/utils/logging/correlationId";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "store/api/baseQueryWithAuth.errors",
);

interface ProblemDetailsLike {
  type?: unknown;
  title?: unknown;
  status?: unknown;
  detail?: unknown;
  errorCode?: unknown;
  correlationId?: unknown;
  instance?: unknown;
  message?: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasCompleteProblemDetailsShape = (
  problem: ProblemDetailsLike | null,
): boolean =>
  typeof problem?.type === "string" &&
  typeof problem.title === "string" &&
  typeof problem.status === "number" &&
  typeof problem.detail === "string" &&
  typeof problem.errorCode === "string" &&
  typeof problem.correlationId === "string";

const AUTH_RELOGIN_ERROR_CODES = new Set([
  "AUTH_TOKEN_MISSING",
  "UNAUTHORIZED",
  "unauthorized",
]);

const RETRY_AFTER_MINIMUM_MS = 8_000;

const readRetryAfterDeadline = (headers: unknown): number => {
  const now = Date.now();
  const fallback = now + RETRY_AFTER_MINIMUM_MS;
  if (!isRecord(headers)) return fallback;
  const header =
    typeof headers.get === "function"
      ? (headers as { get: (name: string) => unknown }).get("Retry-After")
      : (headers["retry-after"] ?? headers["Retry-After"]);
  const value =
    typeof header === "string" || typeof header === "number"
      ? String(header).trim()
      : "";
  const isSeconds = /^\d+$/u.test(value);
  const requested = isSeconds
    ? Math.min(Number.MAX_SAFE_INTEGER, now + Number(value) * 1_000)
    : Date.parse(value);
  return Number.isFinite(requested) ? Math.max(fallback, requested) : fallback;
};

export const normalizeCorrelationId = (
  value: string | readonly string[] | undefined,
): string | undefined =>
  typeof value === "string"
    ? value
    : Array.isArray(value) && typeof value[0] === "string"
      ? value[0]
      : undefined;

export const normalizeApiError = (args: {
  axiosError: AxiosError;
  fallbackMessage: string;
  correlationIdOverride?: string;
}): ApiError => {
  const responseData = args.axiosError.response?.data;
  if (responseData !== undefined && !isRecord(responseData)) {
    logger.error(
      "Received malformed API error payload during normalization",
      undefined,
      {
        operation: "normalizeApiError_malformed_payload",
        payloadType: typeof responseData,
      },
    );
  }
  const problem = isRecord(responseData)
    ? (responseData as ProblemDetailsLike)
    : null;
  const responseCorrelationId =
    args.correlationIdOverride ??
    readCorrelationIdFromHeaders(args.axiosError.response?.headers);

  const detail =
    typeof problem?.detail === "string" ? problem.detail : undefined;
  const message =
    typeof problem?.message === "string" ? problem.message : undefined;
  const title = typeof problem?.title === "string" ? problem.title : undefined;
  const normalizedMessage = detail ?? message ?? title ?? args.fallbackMessage;
  const publicError = coercePublicErrorResponse({
    status: args.axiosError.response?.status,
    data: responseData ?? normalizedMessage,
    correlationId: responseCorrelationId ?? null,
  });
  const hasCompleteProblemDetails = hasCompleteProblemDetailsShape(problem);
  const normalizedCode = publicError.errorCode;
  logger.debug("Normalized API error code mapping", {
    operation: "normalizeApiError_code_mapping",
    rawCode: hasCompleteProblemDetails ? (problem?.errorCode ?? null) : null,
    normalizedCode,
    status: args.axiosError.response?.status ?? null,
  });
  const normalizedCorrelationId =
    publicError.correlationId !== "unknown"
      ? publicError.correlationId
      : undefined;
  const normalizedInstance =
    hasCompleteProblemDetails && typeof problem?.instance === "string"
      ? problem.instance
      : undefined;

  const shouldShowAuthRecoveryMessage =
    args.axiosError.response?.status === 401 ||
    AUTH_RELOGIN_ERROR_CODES.has(normalizedCode);
  const displayMessage = formatPublicErrorForDisplay({
    ...publicError,
    message: shouldShowAuthRecoveryMessage
      ? adminUiCopy.authLogin.authSessionLost
      : publicError.message,
  });
  if (
    responseData !== undefined &&
    normalizedMessage === args.fallbackMessage &&
    !problem?.detail &&
    !problem?.message &&
    !problem?.title
  ) {
    logger.warn("Using fallback API error message due to unmapped payload", {
      operation: "normalizeApiError_fallback_message",
      status: args.axiosError.response?.status ?? null,
      hasProblemObject: Boolean(problem),
    });
  }

  return {
    status: args.axiosError.response?.status ?? 500,
    data: responseData,
    message: displayMessage,
    code: normalizedCode,
    ...(normalizedCorrelationId
      ? { correlationId: normalizedCorrelationId }
      : {}),
    ...(normalizedInstance ? { instance: normalizedInstance } : {}),
    // CRITICAL PD-51: retain only the safe deadline, never the raw header, so
    // an open editor can honor a rate-limit response before retrying.
    // https://www.rfc-editor.org/rfc/rfc9110#section-10.2.3
    ...(args.axiosError.response?.status === 429
      ? {
          retryAfterAt: readRetryAfterDeadline(
            args.axiosError.response.headers,
          ),
        }
      : {}),
  };
};
